import { AlertFactory, AlertState } from 'alerts/Alert'
import Document from 'documents/Document'
import DocumentTag from 'documents/DocumentTag'
import { orderBy } from 'lodash'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { ViewDocumentDetailsDelegate } from 'documents/views/DocumentRoot/DocumentDetailsRootViewModel'
import UploadBarViewModel from 'documents/views/DocumentRoot/UploadBar/UploadBarViewModel'
import { JobDocumentsAdapter } from '../../../JobDocumentsViewModelAdapter'
import Job from 'jobs/Job'
import { Id } from 'types/definitions'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import Member from 'iam/models/Member'
import DocumentType from 'documents/DocumentType'
import { DocumentCreationModalViewModel } from 'documents/views/DocumentCreationModal/DocumentCreationModalViewModel'
import TabController from '../../../../infra/TabController'
import Checklist from '../../../Checklist'
import { first, Observable, Subscription } from 'rxjs'
import { DocumentManualVerificationViewModel } from 'jobs/views/JobRoot/Job/DocumentManualVerificationModal/DocumentManualVerificationViewModel'
import DocumentVersion from 'documents/DocumentVersion'
import { WellKnownDocumentTypeShortId } from 'documents/dtos/document-type'

export default class JobDocumentsViewModel {
  /**
   * Manages an Rx subscription while the VM is active.
   */
  private subscription = new Subscription()

  scrollProvider: InfiniteScrollProvider<Document> | null = null

  filterTags: Array<DocumentTag> = []

  selectedDocuments = observable.set<Document>()

  uploadBarVM = new UploadBarViewModel(
    this.adapter.newAlert.bind(this.adapter),
    this.adapter.documentActionsAdapter.uploadNewDocument.bind(
      this.adapter.documentActionsAdapter,
    ),
  )

  documentCreationVM: DocumentCreationModalViewModel | null = null

  confirmDocumentDelete = new ConfirmViewModel()
  confirmDocumentDeleteSelected = new ConfirmViewModel()

  /**
   * TabController for the Documents section tabs.
   */
  documentsTabController = new TabController<DocumentsSectionTab>(() => [
    {
      tab: DocumentsSectionTab.ClientChecklist,
      title: 'Client checklist',
      hasError: () => false,
    },
    {
      tab: DocumentsSectionTab.Documents,
      title: 'Documents',
      hasError: () => false,
    },
  ])

  constructor(
    public job: Job,
    private newAlert: AlertFactory,
    public viewDocumentDetails: ViewDocumentDetailsDelegate,
    private adapter: JobDocumentsAdapter,
    private getCurrentMember: GetCurrentMember,
    private getChecklist: () => Checklist | undefined,
    private clientDataManualVerificationRequired$: Observable<DocumentVersion>,
    public documentManualVerificationVM: DocumentManualVerificationViewModel,
  ) {
    makeObservable(this, {
      filterTags: observable,
      selectedDocuments: observable,
      documentCreationVM: observable,
      checklist: computed,
      collaboratorType: computed,
      documentTags: computed,
      documentTypes: computed,
      supportingItems: computed,
      hasSupportingItems: computed,
      documents: computed,
      filteredDocuments: computed,
      documentTypesForUser: computed,
      jobWorkspaceMember: computed,
      canRemoveDocs: computed,
      stageFiles: action.bound,
      stageFileAsType: action.bound,
      newDocument: action,
      selectDocument: action.bound,
      reuploadDocument: action.bound,
      deleteSelected: action,
      delete: action,
      toggleTagFilter: action,
      tagFilterAll: action,
      viewDocumentCreation: action.bound,
      documentManualVerificationVM: observable,
    })
  }

  activate = task(async () => {
    this.scrollProvider = this.getScrollProvider()
    this.adapter.fetchTags()
    if (this.scrollProvider) {
      await this.scrollProvider.fetch()
    }
    this.uploadBarVM.activate(true)

    this.subscription = new Subscription()

    this.subscription.add(
      this.clientDataManualVerificationRequired$.subscribe(
        (documentVersion) => {
          const document = this.documents.find(
            (doc) => doc.longId === documentVersion.documentLongId,
          )
          if (document) {
            this.documentManualVerificationVM.open(document, this.job)
          }
        },
      ),
    )
  })

  deactivate = () => {
    this.subscription.unsubscribe()
  }

  get checklist() {
    return this.getChecklist()
  }

  get supportingItems() {
    return this.checklist?.sections ?? []
  }

  get hasSupportingItems() {
    return this.supportingItems.length > 0
  }

  /**
   * Get user's collaborator type on the current job.
   */
  get collaboratorType() {
    const userPublicId = this.getCurrentMember().user.publicId
    const collaborator = this.job.collaborators.find(
      (collaborator) => collaborator.member.user.publicId === userPublicId,
    )

    return (
      collaborator?.type ||
      (this.job.isPartOfClientTeam && 'CLIENT') ||
      (this.job.isPartOfProTeam && 'PROVIDER')
    )
  }

  /**
   * Get the first and last names of the clients on the job.
   */
  get clientNames() {
    return this.job.collaborators
      .filter((c) => c.type === 'CLIENT')
      .map((c) => ({
        firstName: c.member.givenName,
        lastName: c.member.familyName,
      }))
  }

  get documentTags() {
    return this.adapter.documentTags
  }

  get documentTypes() {
    return this.adapter.documentTypes
  }

  get documents() {
    return this.adapter.getJobDocuments(Id.assert(this.job.id))
  }

  get filteredDocuments() {
    const filtered = this.documents.filter((document) =>
      this.filterTags.every((tag) => document.tags.includes(tag)),
    )

    return orderBy(filtered, 'dateCreated', 'desc')
  }

  /**
   * Get document types available to the current user.
   * Users with the ADMIN_DOCUMENT_MANAGE permission see all types,
   * otherwise types are filtered by the user's collaborator role.
   */
  get documentTypesForUser() {
    if (this.jobWorkspaceMember.hasPermission('ADMIN_DOCUMENT_MANAGE')) {
      return this.documentTypes.items
    }

    const mappedCollaboratorType = () => {
      switch (this.collaboratorType) {
        case 'CLIENT':
          return 'CLIENT'
        case 'PROVIDER':
          return 'PRO'
        default:
          return undefined
      }
    }

    const collaborator = mappedCollaboratorType()
    return this.documentTypes.filter(
      (type) =>
        type.shortId === WellKnownDocumentTypeShortId.Other ||
        type.uploaderUserType.toUpperCase() === collaborator,
    )
  }

  /**
   * The workspace member for the current user on the workspace
   * that the job belongs to.
   */
  get jobWorkspaceMember() {
    return this.getCurrentMember(this.job.workspace.id) ?? Member.none
  }

  /**
   * Whether the user can remove documents
   */
  get canRemoveDocs() {
    return this.jobWorkspaceMember.hasSomePermissions(
      'DOCS_SELF_REMOVE',
      'DOCS_CLIENT_REMOVE',
      'DOCS_PROVIDER_REMOVE',
    )
  }

  getScrollProvider() {
    return this.adapter.getScrollProvider(Id.assert(this.job.id))
  }

  stageFiles(files: FileList) {
    return this.newDocument(files, undefined)
  }

  stageFileAsType(files: FileList, type: DocumentType) {
    return this.newDocument(files, type)
  }

  viewDocument(document: Document) {
    if (document.latestVersion?.isManualVerificationRequired) {
      this.documentManualVerificationVM.open(document, this.job)
      return
    }

    const viewAsAdmin = this.jobWorkspaceMember.hasPermission(
      'ADMIN_DOCUMENT_MANAGE',
    )
    this.viewDocumentDetails(document, viewAsAdmin)
  }

  async newDocument(files: FileList, type: DocumentType | undefined) {
    if (files.length < 1) {
      return false
    }

    const filesArray = Array.from(files)

    const filesToUpload = filesArray.map((f) => ({
      file: f,
      options: this.adapter.makeAccessOptions(this.job, type),
    }))

    try {
      await this.uploadBarVM.uploadFiles(filesToUpload).finally(() => {
        this.documentCreationVM = null
      })
      return true
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      return false
    }
  }

  archiveDocument(document: Document) {
    return this.adapter.documentActionsAdapter.archiveDocument(document)
  }

  download = (document: Document, inline: boolean) => {
    return this.adapter.documentActionsAdapter.downloadDocument(
      document,
      inline,
    )
  }

  async reuploadDocument(document: Document, files: File) {
    const viewAsAdmin = this.jobWorkspaceMember.hasPermission(
      'ADMIN_DOCUMENT_MANAGE',
    )
    const vm = this.viewDocumentDetails(document, viewAsAdmin)
    const uploadVMClose$ = vm.uploadVersion(files)
    uploadVMClose$.pipe(first()).subscribe(() => vm.close())
  }

  selectDocument(document: Document) {
    if (this.selectedDocuments.has(document)) {
      this.selectedDocuments.delete(document)
    } else {
      this.selectedDocuments.add(document)
    }
    return this.selectedDocuments
  }

  isSelected(document: Document) {
    return this.selectedDocuments.has(document)
  }

  downloadAll = task.resolved(async () => {
    const processingAlert = this.newAlert()
      .saying(
        'Processing',
        'Your file is being created in the background and will download once ready. Please do not close this browser window.',
      )
      .withState(AlertState.Pending)
      .show()

    while (!this.getScrollProvider().isDone) {
      await this.getScrollProvider().more()
    }

    const docs = this.filteredDocuments
      .filter((d) => d.latestVersion && d.latestVersion.downloadable)
      .map((d) => ({
        document_id: d.longId!,
        version_id: d.latestVersion!.id!,
        file_id: d.latestVersion!.files[0].id!,
      }))

    try {
      await this.adapter.documentActionsAdapter.downloadAll(docs)
      processingAlert.dismiss()
    } catch (e) {
      processingAlert.fromError(e).autoDismiss()
    }
  })

  downloadSelected = task.resolved(async () => {
    const processingAlert = this.newAlert()
      .saying(
        'Processing',
        'Your file is being created in the background and will download once ready. Please do not close this browser window.',
      )
      .withState(AlertState.Pending)
      .show()

    const docs = Array.from(this.selectedDocuments).map((document) => ({
      document_id: document.longId!,
      version_id: document.latestVersion!.id!,
      file_id: document.latestVersion!.files[0].id!,
    }))

    try {
      await this.adapter.documentActionsAdapter.downloadAll(docs)
      processingAlert.dismiss()
    } catch (e) {
      processingAlert.fromError(e).autoDismiss()
    }
  })

  deleteSelected = async () => {
    if (!(await this.confirmDocumentDeleteSelected.show())) {
      return
    }

    const docs = Array.from(this.selectedDocuments).map((doc) =>
      this.archiveDocument(doc),
    )

    try {
      await Promise.all(docs)
      this.confirmDocumentDeleteSelected.decline()
      this.selectedDocuments.clear()
    } catch (e) {
      this.adapter.newAlert().fromError(e).show()
      console.error(e)
    }
  }

  async delete(document: Document) {
    if (!(await this.confirmDocumentDelete.show())) {
      return
    }

    try {
      const docTitle = document.title
      await this.archiveDocument(document)

      this.adapter
        .newAlert()
        .withState(AlertState.Success)
        .saying(`Success!  Document ${docTitle} has been removed!`)
        .autoDismiss()
        .show()

      this.confirmDocumentDelete.decline()
    } catch (error) {
      this.adapter.newAlert().fromError(error).show()
      console.error(error)
    }
  }

  toggleTagFilter(tag: DocumentTag) {
    this.filterTags = [tag]
  }

  tagFilterAll() {
    this.filterTags = []
  }

  viewDocumentCreation(files: File[]) {
    this.documentCreationVM = this.createDocumentCreationModalViewModel(files)
    this.documentCreationVM.show()
  }

  createDocumentCreationModalViewModel(files: File[]) {
    return new DocumentCreationModalViewModel(
      files,
      this.documentTypesForUser,
      this.job,
      this.uploadBarVM,
    )
  }
}

/**
 * Documents section tab.
 */
export const enum DocumentsSectionTab {
  ClientChecklist,
  Documents,
}
